/**
 * MBA Lab — pure state/markdown engine for the Team Retrospective
 * Facilitator. No DOM, no storage, no analytics, no timers: everything here
 * is node-importable and deterministic, matching planner-model.js and
 * listening-model.js.
 *
 * Three phases, and only three - Prep, Run, Commit. That is the whole method
 * from Content/Blog/mba-team-retrospectives.md and this file deliberately
 * adds nothing to it. Two consequences worth stating up front, because both
 * look like missing features until you read the article:
 *
 *   1. There are EXACTLY two commitment slots and no way to add a third.
 *      "Two is the limit. Teams that leave with five commitments keep zero."
 *      A grow-the-list button would be the single fastest way to break this
 *      tool, so COMMITMENT_SLOTS is a hard structural constant and every
 *      setter bounds-checks against it rather than trusting an index.
 *   2. There is no timer. The 5/15/10 timebox is display-only (see
 *      retro-prompts.js's TIMEBOXES).
 *
 * PERSISTENCE IS TIERED, and the split is the sensitive part - see
 * stripSessionNotes() below. The Run-phase capture fields hold candid
 * observations about named teammates ("slides always need rework"), written
 * in a room where everyone can see the screen. Those belong to the session
 * they were written in and nothing else. The Commit-phase output - two
 * behavior changes with owners, and when they'll be reviewed - is the exact
 * opposite: it is *meant* to outlive the meeting, because rereading it at the
 * next retro is what makes the loop compound across a two-week gap.
 */

import { CAPTURE_PROMPTS } from './retro-prompts.js';

export const SCHEMA_NAME = 'mbalab.team-retrospective-facilitator';
export const SCHEMA_VERSION = 1;

/**
 * Two, always. Not a default, not a maximum the UI happens to enforce - the
 * cap IS the method. createInitialState() builds exactly this many slots and
 * sanitizeState() re-normalizes any saved state back to exactly this many, so
 * a hand-edited storage payload claiming five commitments still loads as two.
 */
export const COMMITMENT_SLOTS = 2;

export const LIMITS = Object.freeze({
  deliverable: 160,
  /** Capture fields are one theme per line, so they need room for a list. */
  note: 1500,
  commitmentText: 300,
  commitmentOwner: 80,
  reviewMoment: 300,
  // Sized in UTF-8 bytes via TextEncoder, while the character limits above are
  // UTF-16 code units - see planner-model.js's LIMITS comment for why the two
  // don't line up for CJK text. A retro filled to every limit in 3-byte
  // characters is ~15KB, so this leaves generous headroom.
  maxSerializedBytes: 60 * 1024,
});

/** The three Run-phase capture fields, keyed off the prompt list so a prompt
 * can never exist without a field or vice versa. */
const NOTE_KEYS = Object.freeze(CAPTURE_PROMPTS.map((p) => p.key));

/**
 * The KEEP-allowlist for the opt-in localStorage tier. Written as an allowlist
 * (copy these onto a fresh object) rather than a denylist (clone and delete
 * the sensitive ones) on purpose: with a denylist, every field added to this
 * model later would default to being written to disk, and the failure would be
 * silent. With an allowlist, a new field defaults to session-only and the
 * worst case is that someone has to come back here and opt it in deliberately.
 *
 * Four entries. `savedAt` rides along because the persistent tier needs to
 * know how old it is; the other three are the Commit-phase output plus the
 * deliverable name it belongs to, which is what the next retro reads back.
 */
export const KEPT_FIELDS = Object.freeze(['savedAt', 'deliverable', 'commitments', 'reviewMoment']);

function clampText(value, maxLen) {
  return typeof value === 'string' ? value.slice(0, maxLen) : '';
}

function emptyCommitments() {
  return Array.from({ length: COMMITMENT_SLOTS }, () => ({ text: '', owner: '' }));
}

/**
 * Normalizes any input to exactly COMMITMENT_SLOTS slots of {text, owner},
 * dropping extras and padding shortfalls. Every read path runs through this,
 * which is what makes "exactly two" true of loaded state and not just of
 * freshly created state.
 */
function normalizeCommitments(raw) {
  const list = Array.isArray(raw) ? raw : [];
  return Array.from({ length: COMMITMENT_SLOTS }, (_, i) => {
    const slot = list[i] && typeof list[i] === 'object' ? list[i] : {};
    return {
      text: clampText(slot.text, LIMITS.commitmentText),
      owner: clampText(slot.owner, LIMITS.commitmentOwner),
    };
  });
}

/** Prior commitments are read-only context, so only `done` is ever settable.
 * `done` is a tri-state: null = not yet marked, true = did it, false = didn't. */
function normalizePriorCommitments(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.slice(0, COMMITMENT_SLOTS).map((item) => {
    const slot = item && typeof item === 'object' ? item : {};
    return {
      text: clampText(slot.text, LIMITS.commitmentText),
      owner: clampText(slot.owner, LIMITS.commitmentOwner),
      done: slot.done === true ? true : slot.done === false ? false : null,
    };
  });
}

export function createInitialState() {
  const state = {
    schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    savedAt: null,
    // Prep
    deliverable: '',
    priorDeliverable: '',
    priorReviewMoment: '',
    priorCommitments: [],
    // Run (session-only - never persisted, see stripSessionNotes)
    // Commit
    commitments: emptyCommitments(),
    reviewMoment: '',
    /**
     * Whether a human has actually changed something, as opposed to the tool
     * having seeded last retro's commitments into Prep on its own. Load-bearing
     * for analytics: seeding must never look like engagement (see
     * seedFromRemembered) or every returning visitor fires a tool_engage
     * before touching the page.
     */
    userEdited: false,
  };
  for (const key of NOTE_KEYS) state[key] = '';
  return state;
}

/** Marks the state as touched by a real person. Every setter below goes
 * through this; seedFromRemembered() deliberately does not. */
function edited(state, patch) {
  return { ...state, ...patch, userEdited: true };
}

export function setDeliverable(state, value) {
  return edited(state, { deliverable: clampText(value, LIMITS.deliverable) });
}

/** @param {string} key - one of the three CAPTURE_PROMPTS keys. Unknown keys
 * are a no-op rather than a new field, so a typo can't invent a fourth prompt
 * that stripSessionNotes() has never heard of. */
export function setNote(state, key, value) {
  if (!NOTE_KEYS.includes(key)) return state;
  return edited(state, { [key]: clampText(value, LIMITS.note) });
}

function setCommitmentPart(state, index, part, value, maxLen) {
  if (!Number.isInteger(index) || index < 0 || index >= COMMITMENT_SLOTS) return state;
  const commitments = state.commitments.map((slot, i) => (
    i === index ? { ...slot, [part]: clampText(value, maxLen) } : { ...slot }
  ));
  return edited(state, { commitments });
}

export function setCommitmentText(state, index, value) {
  return setCommitmentPart(state, index, 'text', value, LIMITS.commitmentText);
}

export function setCommitmentOwner(state, index, value) {
  return setCommitmentPart(state, index, 'owner', value, LIMITS.commitmentOwner);
}

export function setReviewMoment(state, value) {
  return edited(state, { reviewMoment: clampText(value, LIMITS.reviewMoment) });
}

/** The loop-closer's only mutation: marking a remembered commitment done or
 * not done. The text and owner of a prior commitment are never editable -
 * rewriting last retro's commitment after the fact is exactly the failure the
 * review moment exists to prevent. */
export function setPriorCommitmentDone(state, index, done) {
  if (!Number.isInteger(index) || index < 0 || index >= state.priorCommitments.length) return state;
  const value = done === true ? true : done === false ? false : null;
  const priorCommitments = state.priorCommitments.map((slot, i) => (
    i === index ? { ...slot, done: value } : { ...slot }
  ));
  return edited(state, { priorCommitments });
}

/** Non-empty commitment TEXT only. An owner with no commitment is not a
 * commitment, and a commitment of only whitespace is not one either. */
export function commitmentCount(state) {
  if (!state || !Array.isArray(state.commitments)) return 0;
  return state.commitments.filter((c) => (c?.text || '').trim() !== '').length;
}

export function isComplete(state) {
  return commitmentCount(state) === COMMITMENT_SLOTS;
}

/** How many of the three phases have anything in them - the coarse
 * `sections_started` analytics value, never the content itself. */
export function sectionsStarted(state) {
  const s = state && typeof state === 'object' ? state : {};
  let count = 0;
  const prepTouched = (s.deliverable || '').trim() !== ''
    || (Array.isArray(s.priorCommitments) ? s.priorCommitments : []).some((c) => c?.done != null);
  if (prepTouched) count++;
  if (NOTE_KEYS.some((key) => (s[key] || '').trim() !== '')) count++;
  if (commitmentCount(s) > 0 || (s.reviewMoment || '').trim() !== '') count++;
  return count;
}

export function hasUserEdits(state) {
  return state?.userEdited === true;
}

export function hasAnyContent(state) {
  return sectionsStarted(state) > 0;
}

/**
 * Builds the localStorage payload from the KEPT_FIELDS allowlist. Anything not
 * named there - the three Run-phase capture fields above all - never reaches
 * disk, no matter how it got into the state object.
 */
export function stripSessionNotes(state) {
  const src = state && typeof state === 'object' ? state : {};
  const kept = { schema: SCHEMA_NAME, version: SCHEMA_VERSION };
  for (const field of KEPT_FIELDS) {
    if (field === 'commitments') {
      // Rebuilt slot by slot, not spread - a slot carrying an extra key (an
      // older schema, a hand-edited payload) must not smuggle it onto disk.
      kept.commitments = normalizeCommitments(src.commitments);
    } else if (field === 'savedAt') {
      kept.savedAt = typeof src.savedAt === 'number' ? src.savedAt : null;
    } else if (field === 'deliverable') {
      kept.deliverable = clampText(src.deliverable, LIMITS.deliverable);
    } else if (field === 'reviewMoment') {
      kept.reviewMoment = clampText(src.reviewMoment, LIMITS.reviewMoment);
    }
  }
  return kept;
}

/**
 * Starts a fresh retro that remembers the last one. `remembered` is whatever
 * came back out of the persistent tier (i.e. a stripSessionNotes() payload),
 * so it can only ever carry the four allowlisted fields - there is no path by
 * which last session's candid notes reappear here.
 *
 * The new retro's OWN commitments start empty: this is a new conversation
 * about a new deliverable, and pre-filling it with last time's answers would
 * quietly make "did we do it?" and "what do we commit to now?" the same field.
 *
 * Deliberately does NOT set userEdited - the tool did this, not the user.
 *
 * @param {object|null} remembered
 * @returns {object} a fresh state, seeded or not
 */
export function seedFromRemembered(remembered) {
  const state = createInitialState();
  if (!remembered || typeof remembered !== 'object') return state;

  const prior = normalizeCommitments(remembered.commitments)
    .filter((c) => c.text.trim() !== '')
    .map((c) => ({ text: c.text, owner: c.owner, done: null }));
  if (prior.length === 0) return state;

  return {
    ...state,
    priorCommitments: prior,
    priorDeliverable: clampText(remembered.deliverable, LIMITS.deliverable),
    priorReviewMoment: clampText(remembered.reviewMoment, LIMITS.reviewMoment),
  };
}

export function sanitizeState(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const state = {
    schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    savedAt: typeof r.savedAt === 'number' ? r.savedAt : null,
    deliverable: clampText(r.deliverable, LIMITS.deliverable),
    priorDeliverable: clampText(r.priorDeliverable, LIMITS.deliverable),
    priorReviewMoment: clampText(r.priorReviewMoment, LIMITS.reviewMoment),
    priorCommitments: normalizePriorCommitments(r.priorCommitments),
    commitments: normalizeCommitments(r.commitments),
    reviewMoment: clampText(r.reviewMoment, LIMITS.reviewMoment),
    userEdited: r.userEdited === true,
  };
  for (const key of NOTE_KEYS) state[key] = clampText(r[key], LIMITS.note);
  return state;
}

const MIGRATIONS = {
  // No prior schema version has ever shipped - see charter-model.js's
  // identical note. Proves the mechanism works before it's ever needed.
  0: (state) => ({ ...state, version: 1 }),
};

function migrate(rawState) {
  let state = rawState;
  let migrated = false;
  while (typeof state.version === 'number' && state.version < SCHEMA_VERSION && MIGRATIONS[state.version]) {
    state = MIGRATIONS[state.version](state);
    migrated = true;
  }
  return { state, migrated };
}

/** @returns {{status: 'ok'|'migrated'|'unsupported-newer'|'malformed', state: object|null, raw: string|null}} */
export function parseSavedState(raw) {
  let parsed;
  let rawString;

  if (typeof raw === 'string') {
    rawString = raw;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { status: 'malformed', state: null, raw: rawString };
    }
  } else {
    parsed = raw;
    try {
      rawString = raw != null ? JSON.stringify(raw) : null;
    } catch {
      rawString = null;
    }
  }

  if (
    !parsed ||
    typeof parsed !== 'object' ||
    parsed.schema !== SCHEMA_NAME ||
    !Number.isInteger(parsed.version) ||
    parsed.version < 0
  ) {
    // Number.isInteger(NaN) is false, so a corrupted NaN version is rejected
    // here rather than falling through as if it were already current.
    return { status: 'malformed', state: null, raw: rawString };
  }
  if (parsed.version > SCHEMA_VERSION) {
    return { status: 'unsupported-newer', state: null, raw: rawString };
  }

  const { state: migratedState, migrated } = migrate(parsed);
  return { status: migrated ? 'migrated' : 'ok', state: sanitizeState(migratedState), raw: rawString };
}

export function serializeForSave(state) {
  const json = JSON.stringify(state);
  if (typeof TextEncoder !== 'undefined' && new TextEncoder().encode(json).length > LIMITS.maxSerializedBytes) {
    return null;
  }
  return json;
}

/**
 * The whole tiered-persistence rule, as one pure function over injected store
 * objects (core.js's {save, clear} wrapper shape - nothing here touches
 * window, localStorage, or a timer, so it is node-testable).
 *
 * Both the debounced autosave and the remember-toggle handler call this, and
 * that is the point: the "off" branch clears the persistent key unconditionally
 * rather than merely skipping the write. Skipping is the bug that looks
 * correct - untick the box, and last session's commitments sit on disk until
 * something else happens to save. Routing both callers through one function
 * makes "remember is off" mean "the key is gone", now, every time - including
 * on a call where `state` is too large to safely serialize: the oversized-state
 * guard below exists to stop a half-written SAVE, which has nothing to do with
 * a CLEAR, so the clear is not allowed to depend on it.
 *
 * @param {{state: object, remember: boolean, session?: {save: Function},
 *   persistent?: {save: Function, clear: Function}, now?: number}} args
 * @returns {{saved: boolean, persisted: boolean, cleared: boolean, payload: object|null}}
 */
export function persistState({ state, remember, session, persistent, now = Date.now() }) {
  const toSave = { ...state, savedAt: now };

  if (remember !== true) {
    persistent?.clear();
    if (!serializeForSave(toSave)) return { saved: false, persisted: false, cleared: true, payload: null };
    session?.save(toSave);
    return { saved: true, persisted: false, cleared: true, payload: null };
  }

  if (!serializeForSave(toSave)) return { saved: false, persisted: false, cleared: false, payload: null };
  session?.save(toSave);
  const payload = stripSessionNotes(toSave);
  persistent?.save(payload);
  return { saved: true, persisted: true, cleared: false, payload };
}

/**
 * Escapes markdown-structural characters. House convention is that each model
 * keeps its own copy rather than importing one - see charter-model.js's
 * escapeMarkdown for the original and its reasoning. Identical behaviour:
 * backslash first, then inline structure, then the line-leading block markers
 * CommonMark allows up to 3 leading spaces before.
 */
export function escapeMarkdown(text) {
  const escaped = String(text)
    .replace(/\\/g, '\\\\')
    .replace(/([`*_{}[\]()#+!|<>~&])/g, '\\$1');
  return escaped
    .replace(/^( {0,3})([-=>])/gm, '$1\\$2')
    .replace(/^( {0,3})(\d{1,9})\./gm, '$1$2\\.');
}

/** One theme per line: split, trim, drop blanks. Returns [] for an empty field. */
function themeLines(value) {
  return String(value || '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');
}

/** Placeholders are our own literals, not user text, so they're emitted raw -
 * escaping them would print "\[Nothing captured\]" in the download. */
function pushThemes(lines, heading, value, placeholder) {
  lines.push(`## ${heading}`, '');
  const themes = themeLines(value);
  if (themes.length === 0) {
    lines.push(placeholder, '');
    return;
  }
  for (const theme of themes) lines.push(`- ${escapeMarkdown(theme)}`);
  lines.push('');
}

/**
 * The single markdown rendering used by copy, download, and print. Safe on a
 * completely empty state - every section falls back to a placeholder rather
 * than being dropped, so the downloaded file is still a usable blank template.
 */
export function buildMarkdown(state) {
  const s = sanitizeState(state);
  const lines = [];

  lines.push('# Team retrospective');
  lines.push('');
  lines.push(`*Deliverable: ${s.deliverable ? escapeMarkdown(s.deliverable) : '[Not named]'}*`);
  lines.push('');

  if (s.priorCommitments.length > 0) {
    lines.push('## Last time we committed to', '');
    for (const prior of s.priorCommitments) {
      const box = prior.done === true ? '[x]' : '[ ]';
      const owner = prior.owner ? ` (${escapeMarkdown(prior.owner)})` : '';
      const verdict = prior.done === null ? ' - not reviewed' : prior.done ? ' - done' : ' - not done';
      lines.push(`- ${box} ${escapeMarkdown(prior.text)}${owner}${verdict}`);
    }
    lines.push('');
  }

  // Headings come from CAPTURE_PROMPTS itself, not hand-typed copies - the exported document
  // must never say something different from what the on-screen prompt just asked.
  for (const prompt of CAPTURE_PROMPTS) {
    pushThemes(lines, prompt.label, s[prompt.key], '[Nothing captured]');
  }

  lines.push('## Our two commitments', '');
  s.commitments.forEach((slot, i) => {
    if (!slot.text.trim()) {
      lines.push(`${i + 1}. [Not decided yet]`);
      return;
    }
    const owner = slot.owner ? ` - owner: ${escapeMarkdown(slot.owner)}` : ' - owner: [unassigned]';
    lines.push(`${i + 1}. ${escapeMarkdown(slot.text)}${owner}`);
  });
  lines.push('');

  lines.push('## Review moment', '');
  lines.push(s.reviewMoment ? escapeMarkdown(s.reviewMoment) : '[When will you check these two? Name it now.]');
  lines.push('');

  lines.push('---');
  lines.push('*Built with the free MBA Lab team retrospective facilitator - mbalab.net/tools/team-retrospective-facilitator*');

  return lines.join('\n');
}
